import { useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';

import { isTargetable } from '@/admin/admin.features';
import { useVhAdminStore } from '@/admin/admin.store';
import type { VhAdminAudience, VhAdminAudiencePayload } from '@/admin/admin.types';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminButton } from '@/admin/components/AdminButton';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminModal } from '@/admin/components/AdminModal';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import styles from '@/admin/pages/AudiencesPage.module.scss';
import { formatDate } from '@/utils/formatDate';
import { toast } from '@/utils/toast';

const NAME_MAX_LENGTH = 255;

/** Defaults first, then alphabetical, so a new audience lands somewhere predictable. */
const sortAudiences = (audiences: VhAdminAudience[]) =>
  [...audiences].sort((a, b) => {
    if (a.editable !== b.editable) return a.editable ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

const FeatureList = ({ audience }: { audience: VhAdminAudience }) => {
  if (audience.features.length === 0) {
    return <span className={styles.muted}>No features</span>;
  }

  return (
    <div className={styles.badges}>
      {audience.features.map((feature) => (
        <AdminBadge key={feature}>{feature}</AdminBadge>
      ))}
    </div>
  );
};

type AudienceFormModalProps = {
  audience: VhAdminAudience | null;
  takenNames: string[];
  onClose: () => void;
  onSubmit: (payload: VhAdminAudiencePayload) => Promise<void>;
};

const AudienceFormModal = ({ audience, takenNames, onClose, onSubmit }: AudienceFormModalProps) => {
  const features = useVhAdminStore((state) => state.features);
  const targetable = features.filter(isTargetable);
  const [name, setName] = useState(audience?.name ?? '');
  const [selected, setSelected] = useState<string[]>(audience?.features ?? []);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleFeature = (feature: string) =>
    setSelected((current) =>
      current.includes(feature)
        ? current.filter((selectedName) => selectedName !== feature)
        : [...current, feature],
    );

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name for the audience');
      return;
    }
    if (takenNames.some((taken) => taken.toLowerCase() === trimmed.toLowerCase())) {
      setError('An audience with this name already exists');
      return;
    }

    setError('');
    setSaving(true);
    try {
      await onSubmit({ name: trimmed, features: selected });
      onClose();
    } catch {
      setError(
        audience
          ? "Couldn't save the audience. Check the details and try again"
          : "Couldn't create the audience. Check the details and try again",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={audience ? `Edit ${audience.name}` : 'New audience'}
      onClose={onClose}
      footer={
        <>
          <AdminButton onClick={onClose} disabled={saving}>
            Cancel
          </AdminButton>
          <AdminButton tone="primary" onClick={submit} disabled={saving}>
            {audience ? 'Save' : 'Create audience'}
          </AdminButton>
        </>
      }
    >
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Name</span>
        <input
          autoFocus
          className={styles.input}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          placeholder="Beta testers"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && submit()}
        />
      </label>

      <fieldset className={styles.field}>
        <legend className={styles.fieldLabel}>Features this audience switches on</legend>
        {targetable.length === 0 ? (
          <span className={styles.muted}>No features found</span>
        ) : (
          <div className={styles.featureList}>
            {targetable.map((feature) => (
              <label key={feature.name} className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={selected.includes(feature.name)}
                  onChange={() => toggleFeature(feature.name)}
                />
                <span className={styles.featureName}>{feature.name}</span>
                {feature.type === 'frontend' && <AdminBadge>Frontend</AdminBadge>}
                {feature.beta && <AdminBadge tone="success">Beta</AdminBadge>}
              </label>
            ))}
          </div>
        )}
      </fieldset>

      {error && <p className={styles.error}>{error}</p>}
    </AdminModal>
  );
};

const AudienceActions = ({
  audience,
  onEdit,
  onDelete,
}: {
  audience: VhAdminAudience;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  if (!audience.editable) return <span className={styles.muted}>Built in</span>;

  return (
    <div className={styles.actions}>
      <AdminButton size="sm" onClick={onEdit} aria-label={`Edit ${audience.name}`}>
        <FiEdit2 />
        Edit
      </AdminButton>
      <AdminButton
        size="sm"
        tone="danger"
        onClick={onDelete}
        aria-label={`Delete ${audience.name}`}
      >
        <FiTrash2 />
        Delete
      </AdminButton>
    </div>
  );
};

export const AudiencesPage = () => {
  const {
    audiences,
    audiencesLoading,
    audienceCreators,
    fetchAudiences,
    fetchFeatures,
    createAudience,
    updateAudience,
    deleteAudience,
  } = useVhAdminStore();

  /** `null` while closed, `{ audience: null }` for a create, `{ audience }` for an edit. */
  const [form, setForm] = useState<{ audience: VhAdminAudience | null } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<VhAdminAudience | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAudiences();
    fetchFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(() => sortAudiences(audiences), [audiences]);

  const takenNames = useMemo(
    () =>
      audiences
        .filter((audience) => audience.id !== form?.audience?.id)
        .map((audience) => audience.name),
    [audiences, form],
  );

  const creatorOf = (audience: VhAdminAudience) =>
    audience.created_by ? (audienceCreators[audience.created_by] ?? audience.created_by) : 'System';

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    setDeleting(true);
    try {
      await deleteAudience(pendingDelete.id);
      toast.success(`Audience "${pendingDelete.name}" deleted`);
      setPendingDelete(null);
    } catch {
      toast.error("Couldn't delete the audience. Try again");
    } finally {
      setDeleting(false);
    }
  };

  const submitForm = async (payload: VhAdminAudiencePayload) => {
    if (form?.audience) {
      await updateAudience(form.audience.id, payload);
      toast.success(`Audience "${payload.name}" saved`);
    } else {
      await createAudience(payload);
      toast.success(`Audience "${payload.name}" created`);
    }
  };

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>Audiences</h1>
        <AdminButton tone="primary" onClick={() => setForm({ audience: null })}>
          <FiPlus />
          New audience
        </AdminButton>
      </div>

      <p className={styles.hint}>
        An audience switches its features on for everyone assigned to it on the Users screen. The
        two built-in audiences cannot be renamed, retargeted or deleted.
      </p>

      {audiencesLoading && audiences.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : audiences.length === 0 ? (
        <div className={styles.empty}>No audiences found</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>Audience</AdminTableHeadCell>
                <AdminTableHeadCell>Features</AdminTableHeadCell>
                <AdminTableHeadCell>Created by</AdminTableHeadCell>
                <AdminTableHeadCell>Created</AdminTableHeadCell>
                <AdminTableHeadCell>Actions</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {sorted.map((audience) => (
                  <AdminTableRow key={audience.id}>
                    <AdminTableCell>
                      <span className={styles.audienceName}>{audience.name}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <FeatureList audience={audience} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>{creatorOf(audience)}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>{formatDate(audience.created_at)}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <AudienceActions
                        audience={audience}
                        onEdit={() => setForm({ audience })}
                        onDelete={() => setPendingDelete(audience)}
                      />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {sorted.map((audience) => (
                <AdminCard key={audience.id}>
                  <div className={styles.cardHeader}>
                    <span className={styles.audienceName}>{audience.name}</span>
                    <AudienceActions
                      audience={audience}
                      onEdit={() => setForm({ audience })}
                      onDelete={() => setPendingDelete(audience)}
                    />
                  </div>
                  <FeatureList audience={audience} />
                  <div className={styles.cardMeta}>
                    {creatorOf(audience)} · {formatDate(audience.created_at)}
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>
        </>
      )}

      {form && (
        <AudienceFormModal
          audience={form.audience}
          takenNames={takenNames}
          onClose={() => setForm(null)}
          onSubmit={submitForm}
        />
      )}

      {pendingDelete && (
        <AdminModal
          title={`Delete ${pendingDelete.name}?`}
          onClose={() => setPendingDelete(null)}
          footer={
            <>
              <AdminButton onClick={() => setPendingDelete(null)} disabled={deleting}>
                Cancel
              </AdminButton>
              <AdminButton tone="danger" onClick={confirmDelete} disabled={deleting}>
                Delete
              </AdminButton>
            </>
          }
        >
          <p>
            Everyone assigned to this audience moves back to <strong>User</strong> and loses the
            features it switched on. Feature flags stop targeting it.
          </p>
        </AdminModal>
      )}
    </div>
  );
};
